import type { Resume, ResumeTemplate } from '../../../types'
import type { ReactElement } from 'react'
import { ModernPdf, ModernPreview } from './ModernTemplate'
import { ClassicPdf, ClassicPreview } from './ClassicTemplate'
import { CompactPdf, CompactPreview } from './CompactTemplate'
import { ExecutivePdf, ExecutivePreview } from './ExecutiveTemplate'
import { MinimalPdf, MinimalPreview } from './MinimalTemplate'

/**
 * Return the @react-pdf/renderer Document element for the given resume.
 * Used by `renderResumePdf` to produce a Blob.
 */
export function resumeDocumentFor(resume: Resume): ReactElement {
  switch (resume.template) {
    case 'classic':
      return <ClassicPdf resume={resume} />
    case 'compact':
      return <CompactPdf resume={resume} />
    case 'executive':
      return <ExecutivePdf resume={resume} />
    case 'minimal':
      return <MinimalPdf resume={resume} />
    case 'modern':
    default:
      return <ModernPdf resume={resume} />
  }
}

/**
 * HTML preview component for use in the editor's live preview pane.
 */
export function ResumePreview({ resume }: { resume: Resume }) {
  switch (resume.template) {
    case 'classic':
      return <ClassicPreview resume={resume} />
    case 'compact':
      return <CompactPreview resume={resume} />
    case 'executive':
      return <ExecutivePreview resume={resume} />
    case 'minimal':
      return <MinimalPreview resume={resume} />
    case 'modern':
    default:
      return <ModernPreview resume={resume} />
  }
}

export const TEMPLATE_OPTIONS: { value: ResumeTemplate; label: string; description: string }[] = [
  { value: 'modern', label: 'Modern', description: 'Two-column with sidebar accent' },
  { value: 'classic', label: 'Classic', description: 'Traditional centered header' },
  { value: 'compact', label: 'Compact', description: 'Single column, dense layout' },
  { value: 'executive', label: 'Executive', description: 'Bold dark header band, senior roles' },
  { value: 'minimal', label: 'Minimal', description: 'Clean, generous whitespace' },
]
